"use client";

import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { ContactPopup } from "@/components/shared/ContactPopup";

type ContactPopupContextValue = {
  contactOpen: boolean;
  setContactOpen: (open: boolean) => void;
  /** Opens the drawer and remounts the form (use for footer CTA). */
  openContact: () => void;
};

const ContactPopupContext = createContext<ContactPopupContextValue | null>(null);

export function useContactPopup() {
  const ctx = useContext(ContactPopupContext);
  if (!ctx) throw new Error("useContactPopup must be used within ContactPopupProvider");
  return ctx;
}

export function ContactPopupProvider({ children }: { children: ReactNode }) {
  const [contactOpen, setContactOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const openContact = useCallback(() => {
    setFormKey((k) => k + 1);
    setContactOpen(true);
  }, []);

  const value = useMemo(
    () => ({ contactOpen, setContactOpen, openContact }),
    [contactOpen, openContact],
  );

  return (
    <ContactPopupContext.Provider value={value}>
      {children}
      <ContactPopup open={contactOpen} onOpenChange={setContactOpen} formKey={formKey} />
    </ContactPopupContext.Provider>
  );
}
